import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, Icon, Link, Text, Popover } from '@Sage/hoi-poi-ui';
import colors from 'constants/colors';
import TooltipHoi from 'components/TooltipHoi';
import { getLiteral } from 'utils/getLiteral';
import { Info, ChevronUp } from 'components/SvgIcons';
import './style.scss';

const propTypes = {
    label: PropTypes.any,
    mandatory: PropTypes.bool,
    className: PropTypes.string,
    parentClassName: PropTypes.string,
    description: PropTypes.string,
    labelMode: PropTypes.string,
    isBulkAction: PropTypes.bool,
    canClear: PropTypes.bool,
    willBeCleared: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
};

class Base extends PureComponent {
    constructor(props) {
        super(props);

        this.state = {
            bulkEditable: false,
            isToggled: props.canToggle,
            toDelete: false,
        };
    }

    openBulkFieldsWrapper = () => {
        const { isBulkFieldsWrapper, onClickBulkEditable } = this.props;
        if (isBulkFieldsWrapper) {
            this.onClickBulkEditable();
            onClickBulkEditable && onClickBulkEditable(); // custom onClick for fields with additional stuff
        } else return this.onClickBulkEditable;
    };

    onClear = ({ reset = false } = {}) => {
        const { onClear, onReset } = this.props;
        const clear = reset ? false : !this.state.toDelete;
        onClear && onClear(clear);
        if (clear && onReset) onReset(null);
        this.setState({ toDelete: clear });
    };

    onClickBulkEditable = () => {
        const { onClickBulkEditable } = this.props;
        this.setState({ bulkEditable: true });
        onClickBulkEditable && onClickBulkEditable(); // custom onClick for fields with additional stuff
    };

    onReset = () => {
        const { onReset } = this.props;
        onReset && onReset(null);
        this.onClear({ reset: true });
        this.setState({ bulkEditable: false });
    };

    toggleField = () => {
        this.setState({ isToggled: !this.state.isToggled });
    };

    render() {
        let {
            label,
            mandatory,
            children,
            className,
            parentClassName,
            description,
            labelMode,
            isBulkAction,
            isHighlighted,
            isBulkFieldsWrapper,
            isDuplicate,
            canToggle,
            fieldExtraValue,
            canClear,
            willBeCleared,
        } = this.props;

        const { bulkEditable } = this.state;

        let classes = ['fm-field-container', parentClassName, labelMode || 'horizontal'];
        if (isHighlighted) classes.push('fm-field-container--highlighted');

        let classesToggle = ['fm-field-toggle'];
        if (this.state.isToggled) classesToggle.push('fm-field-toggle--collapsed');

        let fieldInputClasses = ['fm-field-input'];
        if (className) fieldInputClasses.push(className);
        if (isDuplicate) fieldInputClasses.push('fm-field-input__duplicate');
        if (canToggle) fieldInputClasses.push('fm-field-input__can-toggle');
        if (canToggle && this.state.isToggled) fieldInputClasses.push('fm-field-input__collapsed');

        let willBeClearedText = '';
        if (Array.isArray(willBeCleared)) {
            willBeClearedText =
                willBeCleared.length === 1
                    ? [<strong key={willBeCleared[0]}>{willBeCleared[0]}</strong>, '.']
                    : willBeCleared
                          .map((item) => <strong key={item}>{item}</strong>)
                          .reduce((prev, curr) => [prev, ', ', curr])
                          .concat('.');
        }

        return (
            <div className={classes.join(' ')}>
                {(label || label === '') && (
                    <div className="fm-field-label">
                        {label}
                        {!isBulkAction && mandatory && (
                            <span className="fm-asterisk-mandatory">*</span>
                        )}
                        {description && (
                            <span className="fm-field-description">
                                <Popover
                                    className="fm-field-description-tooltip"
                                    content={description}
                                    placement="top"
                                    trigger={['hover']}
                                >
                                    <div className="fm-field-description-icon">
                                        <Info color={colors.grey[500]} />
                                    </div>
                                </Popover>
                            </span>
                        )}
                        {fieldExtraValue && (
                            <span className="fm-field-extra-value">
                                <TooltipHoi
                                    className="fm-field-extra-value-tooltip"
                                    content={fieldExtraValue}
                                    placement="right"
                                >
                                    <div className="fm-field-extra-value-icon">
                                        <Info color={colors.semantic.negative450} />
                                    </div>
                                </TooltipHoi>
                            </span>
                        )}
                        {!isBulkAction && canToggle && (
                            <span className={classesToggle.join(' ')} onClick={this.toggleField}>
                                <ChevronUp />
                            </span>
                        )}
                    </div>
                )}
                <div className={fieldInputClasses.join(' ')}>
                    {isBulkAction && !bulkEditable && (
                        <span className="fm-field-bulk" onClick={this.openBulkFieldsWrapper()}>
                            {getLiteral('placeholder_click_to_edit')}
                        </span>
                    )}
                    {(!isBulkAction || bulkEditable) && children}
                    {isBulkAction && bulkEditable && !isBulkFieldsWrapper && (
                        <div className="fm-field-input-bulk-actions">
                            {Array.isArray(willBeCleared) && (
                                <Text
                                    className="fm-field-input-bulk-warning"
                                    color={colors.semantic.negative450}
                                    type="caption"
                                >
                                    {getLiteral('label_parent_field_with_dependencies')}
                                    <TooltipHoi content={willBeClearedText}>
                                        <span>
                                            <Icon
                                                size="small"
                                                name="info"
                                                color={colors.semantic.negative450}
                                            />
                                        </span>
                                    </TooltipHoi>
                                </Text>
                            )}
                            {canClear && (
                                <div className="fm-field-input-bulk-clear" onClick={this.onClear}>
                                    <Checkbox
                                        checked={this.state.toDelete}
                                        color="actionMajor"
                                        size="small"
                                    />
                                    <Text type="caption">
                                        {getLiteral('label_delete_current_values')}
                                    </Text>
                                </div>
                            )}
                            <Link variation="primary" type="caption" onClick={this.onReset}>
                                {getLiteral('action_cancel')}
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

Base.propTypes = propTypes;

export default Base;
